/**
 * "From the PrintRun catalog" (BLU-1268, concept D): the blank-garment
 * suppliers PrintRun keeps a catalog for, on top of the Suppliers page. No new
 * sidebar item: the catalog lives where suppliers already live.
 *
 * One row per catalog supplier: what is in it, where it is based, whether it
 * is already one of the org's suppliers ("In your suppliers", or "Add to my
 * suppliers"), and "Open catalog". A supplier with no styles yet reads
 * "Coming soon" and offers nothing.
 *
 * Shown to anyone who may view suppliers, except a pure 3PL client (it adds
 * no blanks at a floor it does not own). Adding needs `suppliers.manage`.
 * Hides itself when the catalog is empty; a failed read is a small retry
 * card, never a page error (the org's own list below still works).
 */
import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, Check, Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SectionError } from '@/components/shared/SectionError';
import { StatusPill } from '@/components/shared/StatusPill';
import { toast } from '@/hooks/use-toast';
import { useOrgRoleSurfaces } from '@/hooks/useOrgRoleSurfaces';
import { useRole } from '@/hooks/use-role';
import { useCatalogLinkMutations, useCatalogSuppliers } from '../hooks/useSupplierCatalog';
import type { CatalogSupplier } from '../lib/catalog-picker';
import { catalogHref, countryName } from '../lib/catalog-links';
import { linkErrorText } from '../lib/supplier-catalog-errors';
import { SupplierLogo } from './SupplierLogo';

export function CatalogSuppliersCard() {
  const { t, i18n } = useTranslation('inventory');
  const { can } = useRole();
  const { isPureClient } = useOrgRoleSurfaces();
  const visible = can('suppliers.view') && !isPureClient;
  const canManage = can('suppliers.manage');
  const titleId = useId();
  const query = useCatalogSuppliers(visible);
  const { addToMySuppliers } = useCatalogLinkMutations();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; text: string } | null>(null);

  if (!visible) return null;
  if (!query.isLoading && !query.error && (query.data?.length ?? 0) === 0) return null;

  const add = async (row: CatalogSupplier) => {
    if (pendingId) return;
    setPendingId(row.id);
    setRowError(null);
    try {
      await addToMySuppliers.mutateAsync({ catalogSupplierId: row.id });
      toast({ title: t('supplierCatalog.card.added', { supplier: row.name }) });
    } catch (cause) {
      setRowError({ id: row.id, text: linkErrorText(t, cause, 'add', row.name) });
    } finally {
      setPendingId(null);
    }
  };

  return (
    <section aria-labelledby={titleId} className="mt-6 grid grid-cols-1 gap-3 rounded-lg border bg-card p-4 sm:p-5">
      <header className="grid gap-1">
        <h2 id={titleId} className="flex items-center gap-2 text-base font-semibold">
          <BookOpen className="h-4 w-4 text-threadheads-purple" aria-hidden="true" />
          {t('supplierCatalog.card.title')}
        </h2>
        <p className="max-w-prose text-sm text-muted-foreground">{t('supplierCatalog.card.body')}</p>
      </header>

      {query.isLoading ? (
        <div className="grid gap-2" aria-busy="true">
          <span className="sr-only" role="status">
            {t('supplierCatalog.card.loading')}
          </span>
          {Array.from({ length: 2 }, (_, index) => (
            <Skeleton key={index} className="h-10 w-full animate-pulse" />
          ))}
        </div>
      ) : query.error ? (
        <SectionError
          variant="compact"
          title={t('supplierCatalog.card.loadError')}
          onRetry={() => void query.refetch()}
          retryLabel={t('baseGarments.view.retry')}
        />
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('supplierCatalog.card.colSupplier')}</TableHead>
                <TableHead>{t('supplierCatalog.card.colContents')}</TableHead>
                <TableHead>{t('supplierCatalog.card.colCountry')}</TableHead>
                <TableHead>{t('supplierCatalog.card.colYours')}</TableHead>
                <TableHead className="text-right">
                  <span className="sr-only">{t('supplierCatalog.card.colActions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(query.data ?? []).map((row) => {
                const comingSoon = row.styleCount === 0;
                const country = countryName(row.country, i18n.language);
                const busy = pendingId === row.id;
                const error = rowError?.id === row.id ? rowError.text : null;
                return (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">
                      <span className="flex items-center gap-2">
                        <SupplierLogo url={row.logoUrl} className="hidden sm:block" />
                        <span className="min-w-0 break-words">{row.name}</span>
                      </span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm tabular-nums">
                      {comingSoon ? (
                        <span className="text-muted-foreground">{t('supplierCatalog.card.comingSoon')}</span>
                      ) : (
                        t('supplierCatalog.card.styles', { count: row.styleCount })
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{country ?? '-'}</TableCell>
                    <TableCell>
                      {row.linkedSupplier ? (
                        <Link
                          to={`/suppliers/${row.linkedSupplier.id}`}
                          className="inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          aria-label={t('supplierCatalog.card.openYours', { supplier: row.linkedSupplier.title })}
                        >
                          <StatusPill tone={row.linkedSupplier.isActive ? 'on' : 'off'} icon={Check}>
                            {row.linkedSupplier.isActive
                              ? t('supplierCatalog.card.yours')
                              : t('supplierCatalog.card.yoursInactive')}
                          </StatusPill>
                        </Link>
                      ) : canManage && !comingSoon ? (
                        <div className="grid justify-items-start gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            className="h-8 gap-1.5 px-3 text-xs"
                            disabled={pendingId !== null}
                            aria-busy={busy}
                            onClick={() => void add(row)}
                          >
                            {busy ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                            ) : (
                              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                            )}
                            {t('supplierCatalog.card.addMine')}
                          </Button>
                          {error && (
                            <p role="alert" className="max-w-[18rem] text-xs text-destructive">
                              {error}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {comingSoon ? (
                        <Button type="button" variant="ghost" className="h-8 px-3 text-xs" disabled>
                          {t('supplierCatalog.card.open')}
                        </Button>
                      ) : (
                        <Button asChild variant="ghost" className="h-8 px-3 text-xs text-threadheads-purple">
                          <Link to={catalogHref(row)} aria-label={t('supplierCatalog.card.openAria', { supplier: row.name })}>
                            {t('supplierCatalog.card.open')}
                          </Link>
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}
